import axios from 'axios';
import useTokenStore from './token';
import {COOKIE_NAMES} from "../types/auth";
import {globalShowAlert} from "../components/common/error/AlertContext";
import useUserInfoStore from "./user_info";

// axios 인스턴스 생성 및 기본 설정
export const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://pms.covidata.kr/api/v1', // 백엔드 서버 주소 설정
    withCredentials: true,  // CORS 요청 시 쿠키 포함
    headers: {
        'Content-Type': 'application/json',  // JSON 형식으로 데이터 전송
        'Accept': 'application/json'         // JSON 형식의 응답 요청
    }
});

// 로그아웃 처리 함수
export const handleLogout = () => {
    // 리프레시 토큰 쿠키 삭제
    document.cookie = `${COOKIE_NAMES.REFRESH_TOKEN}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    // 액세스 토큰 초기화
    useTokenStore.getState().setAccessToken('');
    // 사용자 정보 초기화
    useUserInfoStore.getState().clearUserInfo();
    // 세션 스토리지 초기화
    sessionStorage.clear();
    // 로그인 페이지로 리다이렉트
    window.location.href = '/login';
};

// 토큰 갱신 인터셉터 설정
export const setupTokenInterceptor = () => {
    let isUpdatingToken = false;  // 토큰 업데이트 중복 방지 플래그
    let latestToken = '';         // 최신 토큰 저장 변수

    // 응답 인터셉터 설정
    api.interceptors.response.use(
        response => {
            // 응답 헤더에서 새로운 액세스 토큰 확인
            const newAccessToken = response.headers['x-new-accesstoken'] ||
                response.headers['X-New-AccessToken'];

            // 새로운 토큰이 있고 업데이트 중이 아닌 경우
            if (newAccessToken && !isUpdatingToken) {
                isUpdatingToken = true;

                // 최신 토큰과 다른 경우에만 업데이트
                if (newAccessToken !== latestToken) {
                    latestToken = newAccessToken;
                    useTokenStore.getState().setAccessToken(newAccessToken);
                }
                isUpdatingToken = false;
            }

            return response;
        },
        error => Promise.reject(error)
    );
};

// 에러 처리 인터셉터 설정
export const setupAlertInterceptor = () => {
    api.interceptors.response.use(
        response => response,
        async error => {
            // 401 에러(인증 실패)인 경우 토큰 갱신 시도
            if (error.response?.status === 401) {
                return handleTokenError(error);
            }

            if (axios.isAxiosError(error)) {
                // 네트워크 에러 처리
                if (error.code === 'ERR_NETWORK') {
                    globalShowAlert('네트워크 오류가 발생했습니다. 서버가 실행 중인지 확인해주세요.');
                    return Promise.reject(error);
                }

                // 서버 연결 실패 처리
                if (!error.response) {
                    globalShowAlert('서버에 연결할 수 없습니다. 연결 상태와 서버 상태를 확인해주세요.');
                    return Promise.reject(error);
                }

                // API 에러 응답 처리
                if (error.response.data && !error.response.data.success) {
                    // C002 코드는 특별 처리 (얼럿 표시하지 않음)
                    if (error.response.data.data.code === "C002") {
                        return Promise.reject(error);
                    }
                    globalShowAlert(error.response.data.data.message);
                    return Promise.reject(error);
                }
            }

            // 기타 에러 처리
            globalShowAlert('요청 처리 중 오류가 발생했습니다.');
            return Promise.reject(error);
        }
    );
};

// 토큰 관련 에러 처리 함수
const handleTokenError = async (error: any) => {
    const originalRequest = error.config;

    // 재시도 방지
    if (originalRequest._retry) {
        return Promise.reject(error);
    }

    originalRequest._retry = true;

    // 리프레시 토큰 쿠키에서 추출
    const cookies = document.cookie.split(';');
    const refreshToken = cookies
        .find(cookie => cookie.trim().startsWith(`${COOKIE_NAMES.REFRESH_TOKEN}=`))
        ?.split('=')[1];

    // 리프레시 토큰이 없으면 로그아웃
    if (!refreshToken) {
        handleLogout();
        return Promise.reject(error);
    }

    try {
        // 리프레시 토큰을 헤더에 추가
        originalRequest.headers['Refresh-Token'] = refreshToken;

        // 새로운 액세스 토큰이 있으면 저장하고 재요청
        const newAccessToken = error.response?.headers?.['x-new-accesstoken'];
        if (newAccessToken) {
            useTokenStore.getState().setAccessToken(newAccessToken);
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return api(originalRequest);
        }

        // 새로운 토큰이 없으면 로그아웃
        handleLogout();
        return Promise.reject(error);
    } catch (refreshError) {
        // 토큰 갱신 실패 시 로그아웃
        handleLogout();
        return Promise.reject(refreshError);
    }
};

// 요청 인터셉터 설정
api.interceptors.request.use(
    config => {
        // 모든 요청에 액세스 토큰 추가
        const accessToken = useTokenStore.getState().accessToken;
        if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    error => {
        console.error('Request Error:', error);
        return Promise.reject(error);
    }
);